import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  cableConsumer,
  getCableReconnectGeneration,
  subscribeCableReconnect,
} from "@/lib/cable";
import { api } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  pruneDismissedTaskIds,
  readDismissedTaskIds,
  writeDismissedTaskIds,
} from "@/lib/background-tasks-dismissed";

export type BackgroundTaskStatus = "queued" | "running" | "succeeded" | "failed";

export type BackgroundTask = {
  id: number;
  kind: string;
  status: BackgroundTaskStatus;
  title: string;
  detail: string | null;
  progress_current: number;
  progress_total: number;
  error_message: string | null;
  meta?: Record<string, unknown>;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export const REACH_DRAFT_KIND = "reach_draft";

/** 从 reach_draft 任务 meta 读客户/步范围（离开页面后靠 catch-up 恢复） */
export function reachDraftScope(task: BackgroundTask): {
  planStepId: number | null;
  externalUserid: string | null;
  bindingId: number | null;
  supersede: boolean;
} {
  const meta = task.meta ?? {};
  const planRaw = meta.plan_step_id;
  const bindRaw = meta.binding_id;
  return {
    planStepId:
      planRaw != null && planRaw !== "" && Number.isFinite(Number(planRaw))
        ? Number(planRaw)
        : null,
    externalUserid:
      typeof meta.external_userid === "string" ? meta.external_userid : null,
    bindingId:
      bindRaw != null && bindRaw !== "" && Number.isFinite(Number(bindRaw))
        ? Number(bindRaw)
        : null,
    supersede: Boolean(meta.supersede),
  };
}

export function isReachDraftTask(task: BackgroundTask): boolean {
  return task.kind === REACH_DRAFT_KIND;
}

export function isActiveBackgroundTask(task: BackgroundTask): boolean {
  return task.status === "queued" || task.status === "running";
}

type BackgroundTasksContextValue = {
  tasks: BackgroundTask[];
  /** 未过滤 dismiss 的全量（成稿状态机用，面板 dismiss 不影响） */
  allTasks: BackgroundTask[];
  activeCount: number;
  dismissTask: (id: number) => void;
  /** 关闭面板：隐藏当前列表中全部任务（含进行中/卡住），写入 localStorage */
  dismissAll: () => void;
  isKindActive: (kind: string) => boolean;
  activeTaskForKind: (kind: string) => BackgroundTask | undefined;
  /** 进行中的手动成稿（可按客户/计划步过滤） */
  activeReachDrafts: (opts?: {
    externalUserid?: string | null;
    planStepId?: number | null;
  }) => BackgroundTask[];
};

const BackgroundTasksContext = createContext<BackgroundTasksContextValue | null>(null);

function upsertTask(list: BackgroundTask[], task: BackgroundTask): BackgroundTask[] {
  const idx = list.findIndex((t) => t.id === task.id);
  if (idx === -1) return [task, ...list];
  const next = list.slice();
  next[idx] = task;
  return next;
}

export function BackgroundTasksProvider({ children }: { children: ReactNode }) {
  const { accountId } = useAccountWorkspace();
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(() => new Set());
  const [cableGen, setCableGen] = useState(0);
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;

  const catchUp = useCallback(async (aid: string) => {
    try {
      const payload = await api.fetchForAccount<{ background_tasks: BackgroundTask[] }>(
        aid,
        "/account/background_tasks",
      );
      // 账户已切换则丢弃过期响应
      if (String(accountIdRef.current ?? "") !== aid) return;

      const list = payload.background_tasks ?? [];
      setTasks(list);
      // 丢弃已不在 catch-up 窗口内的 dismiss id，避免 localStorage 堆积
      setDismissedIds((prev) => {
        const pruned = pruneDismissedTaskIds(
          prev,
          list.map((t) => t.id),
        );
        if (pruned.size !== prev.size) {
          writeDismissedTaskIds(aid, pruned);
          return pruned;
        }
        for (const id of prev) {
          if (!pruned.has(id)) {
            writeDismissedTaskIds(aid, pruned);
            return pruned;
          }
        }
        return prev;
      });
    } catch {
      // catch-up 失败不打断壳；Cable 仍可推送
    }
  }, []);

  useEffect(() => {
    setTasks([]);
    if (!accountId) {
      setDismissedIds(new Set());
      return;
    }
    setDismissedIds(readDismissedTaskIds(String(accountId)));
    void catchUp(String(accountId));
  }, [accountId, catchUp]);

  useEffect(() => subscribeCableReconnect(() => setCableGen(getCableReconnectGeneration())), []);

  useEffect(() => {
    if (!accountId) return;

    const consumer = cableConsumer();
    if (!consumer) return;

    const subscription = consumer.subscriptions.create(
      { channel: "AccountBackgroundTasksChannel", account_id: Number(accountId) },
      {
        connected() {
          const aid = accountIdRef.current;
          if (aid) void catchUp(String(aid));
        },
        received(data: unknown) {
          if (!data || typeof data !== "object") return;
          const payload = data as { type?: string; task?: BackgroundTask };
          // background_task 与 reach_draft.* 均带 task 快照
          if (!payload.task?.id) return;
          if (
            payload.type !== "background_task" &&
            payload.type !== "reach_draft.started" &&
            payload.type !== "reach_draft.finished" &&
            payload.type !== "reach_draft.failed"
          ) {
            return;
          }
          setTasks((current) => upsertTask(current, payload.task!));
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [accountId, cableGen, catchUp]);

  // 已 dismiss 的任务（含进行中卡住）从面板隐藏；Job 仍在后台跑，isKindActive 仍看全量
  const visibleTasks = useMemo(
    () => tasks.filter((t) => !dismissedIds.has(t.id)),
    [tasks, dismissedIds],
  );

  const activeCount = useMemo(
    () => visibleTasks.filter((t) => t.status === "queued" || t.status === "running").length,
    [visibleTasks],
  );

  const persistDismissed = useCallback((next: Set<number>) => {
    const aid = accountIdRef.current;
    if (aid) writeDismissedTaskIds(String(aid), next);
  }, []);

  const dismissTask = useCallback(
    (id: number) => {
      setDismissedIds((prev) => {
        const next = new Set(prev).add(id);
        persistDismissed(next);
        return next;
      });
    },
    [persistDismissed],
  );

  const dismissAll = useCallback(() => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      tasks.forEach((t) => next.add(t.id));
      persistDismissed(next);
      return next;
    });
  }, [tasks, persistDismissed]);

  const isKindActive = useCallback(
    (kind: string) =>
      tasks.some((t) => t.kind === kind && (t.status === "queued" || t.status === "running")),
    [tasks],
  );

  const activeTaskForKind = useCallback(
    (kind: string) =>
      tasks.find((t) => t.kind === kind && (t.status === "queued" || t.status === "running")),
    [tasks],
  );

  const activeReachDrafts = useCallback(
    (opts?: { externalUserid?: string | null; planStepId?: number | null }) => {
      return tasks.filter((t) => {
        if (!isReachDraftTask(t) || !isActiveBackgroundTask(t)) return false;
        const scope = reachDraftScope(t);
        if (opts?.planStepId != null) {
          return scope.planStepId === opts.planStepId;
        }
        if (opts?.externalUserid) {
          return scope.externalUserid === opts.externalUserid;
        }
        return true;
      });
    },
    [tasks],
  );

  const value = useMemo(
    () => ({
      tasks: visibleTasks,
      allTasks: tasks,
      activeCount,
      dismissTask,
      dismissAll,
      isKindActive,
      activeTaskForKind,
      activeReachDrafts,
    }),
    [
      visibleTasks,
      tasks,
      activeCount,
      dismissTask,
      dismissAll,
      isKindActive,
      activeTaskForKind,
      activeReachDrafts,
    ],
  );

  return (
    <BackgroundTasksContext.Provider value={value}>{children}</BackgroundTasksContext.Provider>
  );
}

export function useBackgroundTasks() {
  const ctx = useContext(BackgroundTasksContext);
  if (!ctx) {
    throw new Error("useBackgroundTasks must be used within BackgroundTasksProvider");
  }
  return ctx;
}

/** 企微页等：在 Provider 外可选读取（无 Provider 时返回空实现） */
export function useBackgroundTasksOptional() {
  return useContext(BackgroundTasksContext);
}
